/** High-contrast status colors for HPC admin (dark dashboard). */

export const STATUS_FILTER_TABS = [
  { label: "All", value: "all", accent: "#868dfb" },
  { label: "Pending", value: "pending", accent: "#f5a623" },
  { label: "Under Review", value: "under_review", accent: "#4dabf7" },
  { label: "Active", value: "active", accent: "#4cceac" },
];

export const STATUS_CHIP_STYLES = {
  pending: { bg: "#f5a623", color: "#1a1200", border: "#f5a623" },
  under_review: { bg: "#1976d2", color: "#ffffff", border: "#4dabf7" },
  active: { bg: "#2e7c67", color: "#ffffff", border: "#4cceac" },
  expired: { bg: "#525252", color: "#f0f0f0", border: "#858585" },
  frozen: { bg: "#0b6e8a", color: "#ffffff", border: "#4fc3f7" },
  grace_period: { bg: "#8a5a00", color: "#ffffff", border: "#f5a623" },
  suspended: { bg: "#8b1e1e", color: "#ffffff", border: "#ff6b6b" },
  cancelled: { bg: "#3d3d3d", color: "#e0e0e0", border: "#9e9e9e" },
  inactive: { bg: "#3d3d3d", color: "#e0e0e0", border: "#bdbdbd" },
  blacklisted: { bg: "#000000", color: "#ff6b6b", border: "#ff6b6b" },
};

/** Membership lifecycle status names (admin statuses and calculated states). */
export const MEMBERSHIP_STATUS_LABELS = {
  prospect: "Prospect",
  pending_registration: "Pending Registration",
  pending_payment: "Pending Payment",
  pending: "Pending",
  under_review: "Under Review",
  active: "Active",
  active_founding: "Active - Founding Member",
  active_athlete: "Active - Athlete",
  active_corporate: "Active - Corporate",
  staff_member: "Staff Member",
  complimentary: "Complimentary",
  grace_period: "Grace Period",
  suspended: "Suspended",
  expired: "Expired",
  cancelled: "Cancelled",
  inactive: "Inactive",
  blacklisted: "Blacklisted",
  frozen: "Frozen",
};

export const membershipStatusLabel = (status) =>
  MEMBERSHIP_STATUS_LABELS[status] || formatStatusLabel(status);

/**
 * Chip text for a registration: the specific membership label (e.g. "Active - Athlete")
 * while the member is effectively active, otherwise the effective status.
 */
export const getStatusChipLabel = (registration) => {
  const status = getDisplayStatus(registration);
  const label = registration?.membership_label;
  if (status === "active" && label && label !== "active") return membershipStatusLabel(label);
  return membershipStatusLabel(status);
};

/** Membership hold states (separate from the registration status). */
export const HOLD_STATE_META = {
  scheduled: { label: "Scheduled", color: "#f5a623" },
  effective: { label: "Frozen now", color: "#4fc3f7" },
  ended: { label: "Ended", color: "#9e9e9e" },
  cancelled: { label: "Cancelled", color: "#ff6b6b" },
  invalid: { label: "Invalid", color: "#ff6b6b" },
  not_credited: { label: "Not credited", color: "#ff6b6b" },
};

const needsDarkText = (accent) => accent === "#f5a623";

export const getFilterToggleSx = (accent) => {
  const unselected = getFilterTabSx(accent, false);
  const selected = getFilterTabSx(accent, true);
  return {
    ...unselected,
    "&.Mui-selected": {
      ...selected,
    },
    "&.Mui-selected:hover": {
      ...selected["&:hover"],
    },
  };
};

export const getFilterTabSx = (accent, selected) => {
  const darkText = needsDarkText(accent);
  if (selected) {
    return {
      backgroundColor: accent,
      color: darkText ? "#1a1200" : "#ffffff",
      border: `2px solid ${accent}`,
      fontWeight: 700,
      boxShadow: `0 0 0 1px ${accent}55, 0 4px 12px ${accent}44`,
      "&:hover": {
        backgroundColor: accent,
        opacity: 0.92,
      },
    };
  }
  return {
    backgroundColor: `${accent}1a`,
    color: accent,
    border: `2px solid ${accent}`,
    fontWeight: 600,
    opacity: 0.95,
    "&:hover": {
      backgroundColor: `${accent}33`,
      color: accent,
      border: `2px solid ${accent}`,
    },
  };
};

export const getStatusChipSx = (status) => {
  const style = STATUS_CHIP_STYLES[status] || {
    bg: "#525252",
    color: "#f0f0f0",
    border: "#858585",
  };
  return {
    backgroundColor: style.bg,
    color: style.color,
    border: `1px solid ${style.border}`,
    fontWeight: 700,
    textTransform: "capitalize",
    letterSpacing: "0.02em",
  };
};

export const formatStatusLabel = (status) =>
  (status || "")
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Calculated membership state (effective_status / days_until_expiry / expiry_known).
 * Older API responses without these fields fall back to the stored status.
 */
export const hasMembershipState = (registration) =>
  Boolean(registration) &&
  registration.effective_status !== undefined &&
  registration.expiry_known !== undefined;

/** Status to show on chips: effective_status when available, otherwise the stored status. */
export const getDisplayStatus = (registration) =>
  hasMembershipState(registration) && registration.effective_status
    ? registration.effective_status
    : registration?.status;

/** True when the shown status differs from the recorded (stored) status. */
export const isStatusDerived = (registration) =>
  hasMembershipState(registration) &&
  Boolean(registration.effective_status) &&
  registration.effective_status !== registration.status;

/**
 * True when the displayed activation/expiry dates were filled in by the backend's
 * legacy fallback rather than read from stored values (expiry not known, yet a date is present).
 */
export const hasEstimatedMembershipDates = (registration) =>
  hasMembershipState(registration) &&
  registration.expiry_known === false &&
  Boolean(registration.expiry_date);

/**
 * Expiry date to display. Hold-aware responses carry ``effective_expiry_date`` (stored
 * expiry + credited frozen days); otherwise the stored expiry is shown only when it is a
 * real stored value (never a legacy fallback estimate). Returns null when unknown.
 */
export const getEffectiveExpiryDate = (registration) => {
  if (!registration) return null;
  if (registration.effective_expiry_date !== undefined) return registration.effective_expiry_date || null;
  if (!hasMembershipState(registration)) return registration.expiry_date || null;
  return registration.expiry_known && !hasEstimatedMembershipDates(registration)
    ? registration.expiry_date
    : null;
};

/**
 * Human-readable expiry countdown, or null when nothing reliable can be said.
 * Unknown expiry is only reported for active members; pending/under-review
 * members have no expiry yet.
 */
export const formatExpiryCountdown = (registration) => {
  if (!hasMembershipState(registration)) return null;
  if (registration.expiry_known === false) {
    return getDisplayStatus(registration) === "active" ? "Expiry date unknown" : null;
  }
  const days = registration.days_until_expiry;
  if (typeof days !== "number") return null;
  if (days > 1) return `Expires in ${days} days`;
  if (days === 1) return "Expires tomorrow";
  if (days === 0) return "Expires today";
  if (days === -1) return "Expired 1 day ago";
  return `Expired ${Math.abs(days)} days ago`;
};

/** Text colour for the countdown (amber when close or unknown, red when past). */
export const getExpiryCountdownColor = (registration) => {
  if (!hasMembershipState(registration)) return undefined;
  if (registration.expiry_known === false) return "#f5a623";
  const days = registration.days_until_expiry;
  if (typeof days !== "number") return undefined;
  if (days < 0) return "#ff6b6b";
  if (days <= 7) return "#f5a623";
  return "#4cceac";
};

/** Map UI filter value to API query param. */
export const filterValueToApiStatus = (filterValue) =>
  filterValue === "all" ? "" : filterValue;

/** Map API / empty filter to UI toggle value. */
export const apiStatusToFilterValue = (apiStatus) => apiStatus || "all";

/** Visible action buttons on the dark HPC admin dashboard. */
export const backButtonSx = {
  color: "#ffffff",
  borderColor: "#868dfb",
  backgroundColor: "rgba(134, 141, 251, 0.18)",
  borderWidth: 2,
  fontWeight: 700,
  textTransform: "none",
  px: 2,
  "&:hover": {
    borderColor: "#a4a9fc",
    backgroundColor: "rgba(134, 141, 251, 0.35)",
    color: "#ffffff",
    borderWidth: 2,
  },
};

export const activateButtonSx = {
  backgroundColor: "#2e7c67",
  color: "#ffffff",
  fontWeight: 700,
  textTransform: "none",
  border: "2px solid #4cceac",
  boxShadow: "0 4px 12px rgba(76, 206, 172, 0.35)",
  "&:hover": {
    backgroundColor: "#3da58a",
    border: "2px solid #70d8bd",
  },
  "&.Mui-disabled": {
    color: "#e0e0e0",
    backgroundColor: "#525252",
    border: "2px solid #858585",
  },
};

export const cancelButtonSx = {
  color: "#e0e0e0",
  borderColor: "#858585",
  backgroundColor: "rgba(255, 255, 255, 0.06)",
  borderWidth: 2,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": {
    borderColor: "#c2c2c2",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    color: "#ffffff",
    borderWidth: 2,
  },
};

export const viewProfileButtonSx = {
  backgroundColor: "#f5a623",
  color: "#1a1200",
  fontWeight: 700,
  textTransform: "none",
  border: "2px solid #f5a623",
  boxShadow: "0 4px 12px rgba(245, 166, 35, 0.35)",
  "&:hover": {
    backgroundColor: "#ffb84d",
    border: "2px solid #ffb84d",
    color: "#1a1200",
  },
};
